import React from 'react';
import { Icon, Message, PageSubtitle, PageTitle } from '@fasstech/middle-design-system';
import { FormProvider } from 'react-hook-form';
import useSimulationPricingsGroupHealthForm from './hooks/useSimulationPricingsGroupHealthForm.js';
import { equals, isEmpty, map, not } from 'ramda';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import Module from './components/Module.jsx';
import PricingHeader from './components/PricingHeader.jsx';
import PricingSelector from './components/PricingSelector.jsx';
import Options from './components/Options.jsx';
import Budget from './components/Budget.jsx';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import useDisplayedPricing from './hooks/useDisplayedPricings.js';
import UnauthorizedModal from '@@viewCommonComponents/UnauthorizedModal.jsx';

const SimulationPricingGroupHealthForm = ({
  getPricings,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean
}) => {
  const {
    form,
    onSubmit,
    $onClickPrev,
    isValid,
    maxNumberOfProducts,
    pricings,
    tariffStructures,
    selectedTariffStructure,
    options,
    moduleCodes,
    minimumCCNIndex,
    isSelected,
    findModuleLabel,
    findModuleGuarantees,
    selectedOptions,
    toggleOption,
    displayErrorPrices,
    setSelectedPricingCodes,
    selectedPricingCodes,
    setSelectedOptions,
    effectDate,
    companyCcnHasAgreement,
    isUnauthorized,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage
  } = useSimulationPricingsGroupHealthForm({
    getMiddleNumber,
    getPricings
  });

  const numberOfDisplayedPricings = 4;
  const {
    displayedPricings,
    updatePricingsTabsPagination,
    isPricingsTabsButtonDisabled
  } = useDisplayedPricing(pricings, numberOfDisplayedPricings);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout
          withFormLayout={false}
          className={'flex flex-col items-center w-full gap-6'}
        >
          <Icon name="circleGuarantees" size={100} />
          <PageTitle>
            {getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_TITLE')}
          </PageTitle>
          <PageSubtitle>
            {equals(maxNumberOfProducts, 1)
              ? getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_SINGULAR'
              )
              : getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_PLURAL',
                {
                  numberOfProducts: maxNumberOfProducts
                }
              )}
          </PageSubtitle>
          <div className={'flex flex-col items-center w-full gap-6'}>
            <div className={'w-full overflow-visible'}>
              {pricings.length > numberOfDisplayedPricings && (
                <TabsPagination
                  className="visible flex justify-end gap-4"
                  updateVisibility={updatePricingsTabsPagination}
                  isButtonDisabled={isPricingsTabsButtonDisabled}
                />
              )}
              <PricingHeader
                pricings={pricings}
                updateVisibility={updatePricingsTabsPagination}
                isButtonDisabled={isPricingsTabsButtonDisabled}
                displayedPricings={displayedPricings}
                getMiddleText={getMiddleText}
                form={form}
                getMiddleBoolean={getMiddleBoolean}
                isSelected={isSelected}
                minimumCCNIndex={minimumCCNIndex}
                tariffStructures={tariffStructures}
                selectedTariffStructure={selectedTariffStructure}
                setSelectedOptions={setSelectedOptions}
                effectDate={effectDate}
                companyCcnHasAgreement={companyCcnHasAgreement}
              />
              <div className="flex gap-y-4 flex-col">
                {map((moduleCode) => (
                  <Module
                    key={moduleCode}
                    moduleCode={moduleCode}
                    displayedPricings={displayedPricings}
                    selectedPricingCodes={selectedPricingCodes}
                    findModuleLabel={findModuleLabel}
                    findModuleGuarantees={findModuleGuarantees}
                  />
                ))(moduleCodes)}
              </div>
              <PricingSelector
                pricings={pricings}
                displayedPricings={displayedPricings}
                selectedPricingCodes={selectedPricingCodes}
                onSelect={setSelectedPricingCodes}
                minimumCCNIndex={minimumCCNIndex}
                maxNumberOfProducts={maxNumberOfProducts}
              />
              <Options
                getMiddleText={getMiddleText}
                displayedPricings={displayedPricings}
                options={options}
                selectedTariffStructure={selectedTariffStructure}
                isSelected={isSelected}
                selectedOptions={selectedOptions}
                toggleOption={toggleOption}
                pricings={pricings}
                minimumCCNIndex={minimumCCNIndex}
                effectDate={effectDate}
              />
            </div>
            <Budget
              getMiddleText={getMiddleText}
              form={form}
              displayedPricings={displayedPricings}
              selectedTariffStructure={selectedTariffStructure}
              isSelected={isSelected}
            />
          </div>
        </PageLayout>
        {displayErrorPrices && (
          <Message
            className="ml-auto"
            key={'priceError'}
            variant="error"
            label={'Les tarifs demandés ne sont pas disponibles.'}
          />
        )}
        <ButtonContainer
          $onPreviousButtonClick={$onClickPrev}
          getMiddleText={getMiddleText}
          nextButtonLabel={getMiddleText(
            'GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL'
          )}
          nextButtonIsDisabled={
            displayErrorPrices || isEmpty(selectedPricingCodes) || isUnauthorized
          }
          isFormError={not(isValid)}
          displayResumeButton
        />
      </form>
      <UnauthorizedModal
        isDisplayed={!!unauthorizedModalMessage}
        unauthorizedModalMessage={unauthorizedModalMessage}
        setUnauthorizedModalMessage={setUnauthorizedModalMessage}
      />
    </FormProvider>
  );
};

export default SimulationPricingGroupHealthForm;
